import { ReactNode } from "react";
import { useAuth } from "@/shared/hooks/useAuth";
import { Card, CardContent } from "@/shared/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/shared/components/ui/button";

interface RequireAdminRoleProps {
  children: ReactNode;
  fallbackMessage?: string;
}

export function RequireAdminRole({
  children,
  fallbackMessage = "Admin access required to view this page",
}: RequireAdminRoleProps) {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-gray-600 dark:text-gray-400">
              Verifying permissions...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-6" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Authentication Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Please log in to continue
            </p>
            <Button
              onClick={() => (window.location.href = "/login")}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              size="lg"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // WP3: Authoritative admin role set — no aliases
  const userRole = String((user as any)?.role || "").toLowerCase();
  const allowedRoles = new Set(["admin", "super_admin", "platform_admin"]);

  if (!allowedRoles.has(userRole)) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Access Denied
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {fallbackMessage}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Your current role:{" "}
              <span className="font-medium">{userRole || "user"}</span>
            </p>
            <Button
              onClick={() => (window.location.href = "/")}
              className="w-full"
              variant="outline"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is authenticated and has admin role
  return <>{children}</>;
}
